import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Link, Notification, NotificationBanner } from '@dbt-labs/sourdough';

import { useLocalStorage } from '../useLocalStorage';
import { CostInsightsBannerType, getCostInsightsUiState } from './costInsightsUiState';
import { FeatureFlags } from './types';
import { useCostInsightsEnablement } from './useCostInsightsEnablement';
import { externalNavigation } from './utils';

const validateDismissedBanners = (value: any): string[] | null =>
  Array.isArray(value) ? value : null;
const defaultDismissedBanners: string[] = [];

const getBannerNotification = ({
  bannerType,
  accountId,
  projectId,
  jobId,
  navigate,
  variant = 'default',
}: {
  bannerType: CostInsightsBannerType;
  accountId: number;
  projectId: number;
  jobId?: number;
  navigate: (path: string) => void;
  variant?: 'default' | 'inline';
}): Notification => {
  switch (bannerType) {
    case 'sao': {
      const href = jobId
        ? `/deploy/${accountId}/projects/${projectId}/jobs/${jobId}/settings`
        : `/deploy/${accountId}/projects/${projectId}/jobs/#create-job`;
      return {
        type: 'info' as const,
        header: (
          <div>
            Enable state-aware orchestration to reduce build costs by up to 15% or more{' '}
            -{' '}
            <Link
              to="https://docs.getdbt.com/docs/deploy/state-aware-about"
              isInternal={false}
            >
              Learn how
            </Link>
          </div>
        ),
        action: {
          text: 'Enable',
          onClick: () => externalNavigation.navigateTo(href),
          testId: 'enable-state-aware-orchestration-button',
        },
        id: 'sao-banner',
      };
    }
    case 'cost-insights':
      return {
        type: 'info' as const,
        header: 'Enable cost insights to see how SAO is optimizing your builds',
        action: {
          text: 'Enable cost insights',
          onClick: () => navigate(`/settings/accounts/${accountId}/pages/account`),
          testId: 'enable-cost-insights-button',
        },
        id: 'cost-insights-banner',
      };
    case 'permissions':
      return {
        type: 'info' as const,
        header: (
          <div>
            Dollar amounts are not visible with your current permissions. Contact your
            admin to request{' '}
            <Link
              to="https://docs.getdbt.com/docs/explore/set-up-cost-insights#assign-required-permissions"
              target="_blank"
              isInternal={false}
            >
              access
            </Link>
          </div>
        ),
        id: 'permissions-banner',
      };
    // default to fusion banner
    default: {
      const dashboardUrl = `/dashboard/${accountId}/projects/${projectId}`;

      if (variant === 'inline') {
        return {
          type: 'info' as const,
          header:
            'Enable the dbt Fusion engine to reduce build costs by up to 15% or more',
          helperText: (
            <span>
              Follow the steps in the{' '}
              <Link to={dashboardUrl} isInternal={false}>
                Fusion checklist
              </Link>{' '}
              to enable for this project.
            </span>
          ),
          id: 'fusion-banner',
        };
      }

      return {
        type: 'info' as const,
        header: (
          <div>
            Enable the dbt Fusion engine to reduce build costs by up to 15% or more -{' '}
            <Link
              to="https://docs.getdbt.com/docs/deploy/state-aware-about"
              isInternal={false}
            >
              Learn how
            </Link>
          </div>
        ),
        action: {
          text: 'Enable Fusion',
          onClick: () => externalNavigation.navigateTo(dashboardUrl),
          testId: 'enable-fusion-button',
        },
        id: 'fusion-banner',
      };
    }
  }
};

/**
 * Hook to render the cost insights banner.
 * @param accountId - The account ID
 * @param projectId - The project ID
 * @param environmentId - The environment ID
 * @param featureFlags - The feature flags
 * @param jobId - Optional. If provided and SAO banner is active, links to this job's config page.
 * @param pause - Optional. If true, pauses all data fetching queries.
 * @param variant - Optional. 'default' shows CTA button; 'inline' shows text with checklist link (for dashboard context).
 * @returns The cost insights banner. Returns null if no banner should be shown.
 */
export const useCostInsightsBanner = ({
  accountId,
  projectId,
  environmentId,
  featureFlags,
  jobId,
  pause,
  variant = 'default',
}: {
  accountId: number;
  environmentId: number;
  projectId?: number;
  featureFlags: FeatureFlags;
  jobId?: number;
  pause?: boolean;
  variant?: 'default' | 'inline';
}): React.ReactElement | null => {
  const navigate = useNavigate();
  const isEnvironmentResolved = environmentId != null && environmentId !== 0;

  // Track dismissed banners in localStorage
  const [dismissedBanners, setDismissedBanners] = useLocalStorage<string[]>(
    `dbt-cloud.metadata-shared.cost-insights-dismissed-banners-${accountId}-${projectId}`,
    validateDismissedBanners,
    defaultDismissedBanners,
  );

  const costInsightsEnablement = useCostInsightsEnablement({
    accountId,
    projectId,
    environmentId,
    featureFlags,
    pause,
  });

  const isEnablementResolved =
    isEnvironmentResolved && !costInsightsEnablement.isLoading;

  // Handler to dismiss a banner
  const handleDismiss = useCallback(
    (bannerType: CostInsightsBannerType) => {
      setDismissedBanners((prev) => {
        if (!prev.includes(bannerType)) {
          return [...prev, bannerType];
        }
        return prev;
      });
    },
    [setDismissedBanners],
  );

  // Avoid briefly rendering the wrong banner while environments (and therefore Fusion/SAO state)
  // are still resolving. In that state, `isUsingFusion` defaults to false, which would
  // incorrectly show the "Enable Fusion" banner.
  const uiState = useMemo(() => {
    if (!isEnablementResolved) {
      return {
        bannerType: null,
        shouldShowBanner: false,
        metricVisibility: 'none' as const,
      };
    }

    const state = getCostInsightsUiState({
      isSaoEligible: costInsightsEnablement.isSaoEligible,
      isUsingFusion: costInsightsEnablement.isUsingFusion,
      isSaoEnabledForProject: costInsightsEnablement.isSaoEnabledForProject,
      isCostInsightsFeatureEnabled: costInsightsEnablement.hasAccountCostFeatureAccess,
      hasCostInsightsPermission: costInsightsEnablement.canViewProjectCostData,
      canEnableFusion: costInsightsEnablement.canEnableFusion,
    });

    // Don't show banner if it has been dismissed
    if (state.bannerType && dismissedBanners.includes(state.bannerType)) {
      return {
        bannerType: null,
        shouldShowBanner: false,
        metricVisibility: state.metricVisibility,
      };
    }

    return state;
  }, [
    costInsightsEnablement.canViewProjectCostData,
    costInsightsEnablement.hasAccountCostFeatureAccess,
    costInsightsEnablement.isSaoEligible,
    costInsightsEnablement.canEnableFusion,
    costInsightsEnablement.isSaoEnabledForProject,
    costInsightsEnablement.isUsingFusion,
    isEnablementResolved,
    dismissedBanners,
  ]);

  const notification = useMemo(() => {
    if (!uiState.shouldShowBanner || !uiState.bannerType) {
      return null;
    }

    return getBannerNotification({
      bannerType: uiState.bannerType,
      accountId: accountId ?? 0,
      projectId: projectId ?? 0,
      jobId,
      navigate,
      variant,
    });
  }, [
    accountId,
    jobId,
    navigate,
    projectId,
    uiState.bannerType,
    uiState.shouldShowBanner,
    variant,
  ]);

  return useMemo(() => {
    if (!notification) {
      return null;
    }
    return (
      <NotificationBanner
        notification={notification}
        onCloseClick={() => uiState.bannerType && handleDismiss(uiState.bannerType)}
      />
    );
  }, [notification, uiState.bannerType, handleDismiss]);
};
